import React, { useState, useContext, useEffect } from 'react';
import { Form, Radio, Input, Space, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import GenericDialog from "@common/GenericDialog";
import { UserContext } from "@context/UserContext";
import axios from "@common/axios";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const SaveSummaryDialog = ({
    visible,
    handleClose,
    startDate,
    endDate,
    classId,
    studentComments,
    classAverages,
    selectedMetrics,
    excludeInactiveStudents,
    includeBelowAverage,
    onSuccess
}) => {
    const { user } = useContext(UserContext);
    const [form] = Form.useForm();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [dateRangeType, setDateRangeType] = useState('fixed');
    const [customRangeNumber, setCustomRangeNumber] = useState(7);
    const [customRangeUnit, setCustomRangeUnit] = useState('days');
    const [saving, setSaving] = useState(false);

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (visible) {
            // Reset to defaults when opening
            setName('');
            setDescription('');
            setDateRangeType('fixed');
            setCustomRangeNumber(7);
            setCustomRangeUnit('days');
            form.resetFields();
        }
    }, [visible, form]);

    // Sync form field with state
    useEffect(() => {
        form.setFieldsValue({ name });
    }, [name, form]);

    const unitOptions = [
        { label: 'Days', value: 'days' },
        { label: 'Weeks', value: 'weeks' },
        { label: 'Months', value: 'months' },
    ];

    const handleSave = async () => {
        // Validate form fields
        try {
            await form.validateFields();
        } catch (error) {
            // Form validation failed, don't proceed
            return;
        }

        // Validate props that aren't in the form
        if (!classId) {
            displayToastMessage('Class ID is required', null, 'error');
            return;
        }

        if (!studentComments || studentComments.length === 0) {
            displayToastMessage('No student comments to save', null, 'error');
            return;
        }

        setSaving(true);

        try {
            const payload = {
                reportName: name,
                classId: classId,
                dateRangeType: dateRangeType,
                studentComments: studentComments,
                classAverages: classAverages,
                selectedMetrics: selectedMetrics || [],
                excludeInactiveStudents: excludeInactiveStudents || false,
                includeBelowAverage: includeBelowAverage !== false,
                studentCount: studentComments.length
            };

            // Only include description if it's not empty
            if (description && description.trim() !== '') {
                payload.description = description.trim();
            }

            // Add date range fields based on type
            if (dateRangeType === 'fixed') {
                payload.startDate = startDate?.format('YYYY-MM-DD');
                payload.endDate = endDate?.format('YYYY-MM-DD');
            } else {
                payload.adaptiveRangeValue = customRangeNumber;
                payload.adaptiveRangeUnit = customRangeUnit;
            }

            const response = await axios.post('/web/ui/report/teacher/activity/saveActivitySummary', payload);

            if (response.data) {
                displayToastMessage('Activity summary saved successfully', null, 'success');
                // Reset form
                setName('');
                setDescription('');
                setDateRangeType('fixed');
                setCustomRangeNumber(7);
                setCustomRangeUnit('days');
                handleClose();

                // Call onSuccess callback with the saved report ID
                if (onSuccess && response.data.activitySummaryId) {
                    onSuccess(response.data.activitySummaryId);
                }
            }
        } catch (error) {
            console.error('Error saving activity summary:', error);
            displayToastMessage(error.response?.data?.error || 'Failed to save activity summary', null, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Convert URL params to dayjs objects if they exist
    const fixedDateRange = startDate && endDate ? [startDate, endDate] : [dayjs().subtract(6, 'day'), dayjs()];

    return (
        <GenericDialog
            title="Save Summary"
            open={visible}
            dialogWidth={500}
            onClose={handleClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: { type: 'text', disabled: saving }
                },
                {
                    name: 'Save',
                    callback: handleSave,
                    extraProps: {
                        type: 'primary',
                        loading: saving,
                        disabled: saving
                    }
                }
            ]}
        >
            <p>Save your summary with this exact date range, or an adaptive date so you can
                easily get new comments each time you generate the report.</p>
            <Form
                form={form}
                layout="vertical"
                className={'ch-form'}
            >
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                        { required: true, message: 'Please enter a report name' },
                        { whitespace: true, message: 'Report name cannot be empty' }
                    ]}
                >
                    <Input
                        placeholder="Enter report name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Description">
                    <TextArea
                        placeholder="Enter report description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                    />
                </Form.Item>

                <Form.Item label="Date Range Type">
                    <Radio.Group
                        value={dateRangeType}
                        onChange={(e) => setDateRangeType(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio value="fixed">Fixed Date Range</Radio>
                        <Radio value="adaptive">Adaptive Date Range</Radio>
                    </Radio.Group>
                </Form.Item>

                <p>
                    {dateRangeType === 'fixed'
                        ? 'Use fixed date range to return to this exact report at a later date.'
                        : 'Use adaptive date range to always generate a new report based on a date range set below.'}
                </p>

                {dateRangeType === 'fixed' ? (
                    <Form.Item label="Selected Date Range">
                        <RangePicker
                            value={fixedDateRange}
                            format={user?.getDateFormatString?.() || 'DD/MM/YYYY'}
                            style={{ width: '100%' }}
                            disabled
                        />
                    </Form.Item>
                ) : (
                    <Form.Item label="Date Range">
                        <Space.Compact style={{ width: '100%' }}>
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '0 11px',
                                border: '1px solid #d9d9d9',
                                borderRight: 'none',
                                borderRadius: '6px 0 0 6px',
                                background: '#fafafa'
                            }}>
                                Previous
                            </span>
                            <Input
                                type="number"
                                min={1}
                                value={customRangeNumber}
                                onChange={(e) => setCustomRangeNumber(parseInt(e.target.value) || 1)}
                                style={{ borderRadius: 0 }}
                            />
                            <Select
                                options={unitOptions}
                                value={customRangeUnit}
                                onChange={setCustomRangeUnit}
                            />
                        </Space.Compact>
                    </Form.Item>
                )}
            </Form>
        </GenericDialog>
    );
};

export default SaveSummaryDialog;
